// Helper functions for dealing with GroopitFilterDTOs
// When dealing with GroopitFilters we're usually interacting
// with the filters themselves but they can also be wrapped in
// another object which gives them an id, name, and owner when
// they've been saved/published.

import {
  AssignmentDTO,
  ComponentOperationType,
  GroopDTO,
  GroopitFilterAssignmentDisplayDTO,
  GroopitFilterComponentDisplayDTO,
  GroopitFilterDisplayDTO,
  IUserDTO,
  NamedGroopitFilterDisplayDTO,
} from "groopit-js/client";

// This type is just meant to mimic the structure
// of NamedGroopitFilterDisplayDTO so that anything
// that only cares about the filter itself can
// reference it consistently regardless of the type.
interface IUnnamedGroopitFilterDisplayDTO {
  filter: GroopitFilterDisplayDTO;
}

export class UnnamedGroopitFilterDisplayDTO
  implements IUnnamedGroopitFilterDisplayDTO
{
  filter!: GroopitFilterDisplayDTO;

  constructor(data?: IUnnamedGroopitFilterDisplayDTO) {
    if (data) {
      for (const property in data) {
        if (Object.hasOwnProperty.call(data, property))
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (this as any)[property] = (data as any)[property];
      }
    }
    if (!data) {
      this.filter = new GroopitFilterDisplayDTO();
    }
  }
}

export type GroopitFilter =
  | UnnamedGroopitFilterDisplayDTO
  | NamedGroopitFilterDisplayDTO;

export const updateGroopitFilter = (
  existingFilter: GroopitFilter,
  newFilter: GroopitFilterDisplayDTO,
  removeUnnecessaryFilters: boolean,
) => {
  // First, get rid of any unnecessary values. Specifically, components
  // that are set to "Any" can be removed because that's equivalent to
  // not having a filter. Components set to anything other than "None"
  // should have a value. It's easier to handle this here that way the
  // `newFilter` can represent whatever the current UI state is, whether
  // it's ultimately valid or not, and we can clean it up before we
  // we apply it.
  if (removeUnnecessaryFilters) {
    for (let i = newFilter.assignments.length - 1; i >= 0; i--) {
      newFilter.assignments[i].components = newFilter.assignments[
        i
      ].components?.filter((component) => {
        return !isComponentFilterEmpty(component);
      });
    }
  }

  if (existingFilter instanceof NamedGroopitFilterDisplayDTO) {
    return new NamedGroopitFilterDisplayDTO({
      ...existingFilter,
      filter: newFilter,
    });
  } else {
    return new UnnamedGroopitFilterDisplayDTO({
      ...existingFilter,
      filter: newFilter,
    });
  }
};

export const getFilterKeywords = (filter?: GroopitFilter) => {
  if (!filter) {
    return [];
  } else {
    return filter.filter.query;
  }
};

export const isDefaultFilter = (filter: GroopitFilter) => {
  // If it's a named filter it's technically not the default, even
  // if there aren't any filters
  if (filter instanceof NamedGroopitFilterDisplayDTO) {
    return false;
  }
  return !(
    filter.filter.assignments.length ||
    filter.filter.creators.length ||
    filter.filter.dateRange ||
    filter.filter.tags.length ||
    filter.filter.choices.length ||
    filter.filter.feedFilterDataTypes.length ||
    filter.filter.query.length
  );
};

function isEndDateOfNow(endDate?: Date, thresholdSeconds = 1): boolean {
  if (!endDate) return false;
  const now = new Date();
  return Math.abs(now.getTime() - endDate.getTime()) < thresholdSeconds * 1000;
}

export const isDefaultFilterForCurrentTime = (filter: GroopitFilter) => {
  // If it's a named filter it's technically not the default, even
  // if there aren't any filters
  if (filter instanceof NamedGroopitFilterDisplayDTO) {
    return false;
  }
  return Boolean(
    !(
      filter.filter.assignments.length ||
      filter.filter.creators.length ||
      filter.filter.tags.length ||
      filter.filter.choices.length ||
      filter.filter.feedFilterDataTypes.length ||
      filter.filter.query.length
    ) &&
      filter.filter.dateRange &&
      isEndDateOfNow(filter.filter.dateRange.endDate),
  );
};

export const isLegacyFilter = (filter: GroopitFilter) => {
  return (
    filter.filter.creators.length > 0 ||
    Boolean(filter.filter.dateRange) ||
    filter.filter.tags.length > 0 ||
    filter.filter.choices.length > 0 ||
    filter.filter.feedFilterDataTypes.length > 0
  );
};

const isComponentFilterEmpty = (
  component: GroopitFilterComponentDisplayDTO,
) => {
  // If not operation was specified
  if (!component.operation) {
    return true;
  }

  // Certain operation types don't need a value
  if (
    component.operation === ComponentOperationType.None ||
    component.operation === ComponentOperationType.FollowUpComplete ||
    component.operation === ComponentOperationType.FollowUpIncomplete
  ) {
    return false;
  }

  // For any other operation type, we need a valid value

  // Arrays shouldn't be empty
  if (Array.isArray(component.value)) {
    return !component.value.length;
  }

  // Any number is considered valid, including 0
  if (typeof component.value === "number") {
    return false;
  }

  // For all other types (strings, booleans, etc.), we check if it's falsy
  return !component.value;
};

export const isAssignmentFilterEmpty = (
  filter: GroopitFilterAssignmentDisplayDTO | undefined,
  assignment: AssignmentDTO,
) => {
  return (
    !filter ||
    (!filter.creators?.length &&
      !filter.components?.filter((c) => !isComponentFilterEmpty(c)).length &&
      (!filter.componentModelIds?.length ||
        filter.componentModelIds.length == assignment.components.length) &&
      !filter.dateRange)
  );
};

export const isComponentExcludedFromFilter = (
  filter: GroopitFilterDisplayDTO | undefined,
  formId: string,
  componentModel_Id: number,
  parentModelComponentModel_Id: number | undefined,
) => {
  // No filters are being applied, so don't dim anything
  if (!filter || !filter.assignments.length) {
    return false;
  }

  const selectedAssignments = filter.assignments.filter((a) => a.id === formId);

  // There are filters but our assignment isn't selected - we should
  // never hit this because we shouldn't be displaying at all but I
  // guess we'll go ahead and exclude
  if (selectedAssignments.length === 0) {
    return true;
  }

  return isComponentExcludedFromAssignmentFilter(
    selectedAssignments[0],
    componentModel_Id,
    parentModelComponentModel_Id,
  );
};

export const isComponentExcludedFromAssignmentFilter = (
  assignmentFilter: GroopitFilterAssignmentDisplayDTO | undefined,
  componentModel_Id: number,
  parentModelComponentModel_Id: number | undefined,
) => {
  // If there isn't a filter, the default is to not exclude anything
  if (!assignmentFilter) {
    return false;
  }

  // If components are selected and we're not one of them, or we have
  // a parent and it's not one of them then we're excluded
  if (
    assignmentFilter.componentModelIds?.length &&
    (!assignmentFilter.componentModelIds?.includes(componentModel_Id) ||
      (parentModelComponentModel_Id &&
        !assignmentFilter.componentModelIds.includes(
          parentModelComponentModel_Id,
        )))
  ) {
    return true;
  } else {
    return false;
  }
};

export const doesFilterContainsStaleFilters = (
  filter: GroopitFilter,
  group: GroopDTO,
  allPeople: IUserDTO[],
) => {
  if (
    filter.filter.creators.filter((c) => !allPeople.find((p) => p.id === c.id))
      .length
  ) {
    return true;
  }

  for (const assignmentFilter of filter.filter.assignments) {
    const assignment = group.assignments?.find(
      (a) => a.id === assignmentFilter.id,
    );
    if (!assignment) {
      return true;
    }

    if (
      assignmentFilter.creators?.filter(
        (c) => !allPeople.find((p) => p.id === c.id),
      ).length
    ) {
      return true;
    }

    if (
      assignmentFilter.components?.filter(
        (cf) =>
          !assignment.components.find(
            (c) => cf.componentModelId === c.componentModel_Id,
          ),
      ).length
    ) {
      return true;
    }
  }

  return false;
};
